import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { NgOptimizedImage } from "@angular/common";
import { MatTooltip } from "@angular/material/tooltip";
import { TranslatePipe } from "@ngx-translate/core";
import { IconService } from "../../../services/icon.service";

/**
 * Single row inside a top banner: optional title, body (plain or HTML), expand, dismiss.
 */
@Component({
  selector: "app-top-banner-row",
  standalone: true,
  imports: [
    NgOptimizedImage,
    MatTooltip,
    TranslatePipe
  ],
  templateUrl: "./top-banner-row.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./top-banner-row.component.css",
})
export class TopBannerRowComponent implements AfterViewInit, OnChanges {
  @Input() title?: string;
  /** Plain text body (e.g. i18n). Ignored when htmlContent is set. */
  @Input() textContent?: string;
  /** HTML body (e.g. system notifications from API). */
  @Input() htmlContent?: string;

  @Output() dismiss = new EventEmitter<void>();

  @ViewChild("headerRef") headerRef?: ElementRef<HTMLElement>;

  expanded = false;
  isOverflowing = false;
  private resizeObserver: ResizeObserver | null = null;
  private hasMeasured = false;

  constructor(
    private destroyRef: DestroyRef,
    private iconService: IconService,
  ) {
    destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
  }

  get closeIconUrl(): string {
    return this.iconService.getIconPath("close-simple") ?? "";
  }

  get hasHtmlBody(): boolean {
    return !!this.htmlContent?.trim();
  }

  get hasBodyContent(): boolean {
    return this.hasHtmlBody || !!this.textContent?.trim() || !!this.title?.trim();
  }

  get showExpandToggle(): boolean {
    return this.hasBodyContent && this.isOverflowing;
  }

  get isClamped(): boolean {
    return !this.expanded && (this.isOverflowing || !this.hasMeasured);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["title"] || changes["textContent"] || changes["htmlContent"]) {
      this.hasMeasured = false;
      if (this.hasBodyContent) {
        this.scheduleOverflowCheck();
      }
    }
  }

  ngAfterViewInit(): void {
    if (!this.hasBodyContent) {
      return;
    }
    this.scheduleOverflowCheck();
    this.setupResizeObserver();
  }

  onDismiss(): void {
    this.dismiss.emit();
  }

  toggleExpand(): void {
    this.expanded = !this.expanded;
    if (!this.expanded) {
      this.hasMeasured = false;
      this.scheduleOverflowCheck();
    }
  }

  private scheduleOverflowCheck(): void {
    setTimeout(() => this.checkOverflow(), 0);
  }

  private setupResizeObserver(): void {
    const el = this.headerRef?.nativeElement;
    if (!el) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.expanded) {
        this.hasMeasured = false;
        this.scheduleOverflowCheck();
      }
    });
    this.resizeObserver.observe(el);
  }

  private checkOverflow(): void {
    const el = this.headerRef?.nativeElement;
    if (!el || this.expanded || !this.isClamped) {
      return;
    }
    this.isOverflowing = el.scrollHeight > el.clientHeight;
    this.hasMeasured = true;
  }
}
